import { Pencil } from "lucide-react";
import { useState } from "react";
import { useApp } from "@/lib/AppContext";
import { type SourceMaterial } from "@/lib/api";

/**
 * CAP-11 — a recording's title, editable in place. A blank save clears back to the
 * recorder's own name (the upload's filename, or a watch's start timestamp), which is
 * why the input's placeholder is `title` rather than a fixed string: it is whatever the
 * page would fall back to showing.
 */
export function RecordingHeading({
  title,
  recordingId,
  onSaved,
}: {
  title: string;
  recordingId: string;
  onSaved: (updatedTitle: string | null) => void;
}) {
  const { api } = useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [busy, setBusy] = useState(false);

  if (!editing) {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          {title}
        </h1>
        <button
          type="button"
          onClick={() => {
            setDraft(title);
            setEditing(true);
          }}
          aria-label="Rename this recording"
          className="text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
        >
          <Pencil size={16} strokeWidth={2} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-[var(--space-2)]">
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={title}
        className="h-10 flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-3 text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]"
      />
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const updated = await api.renameRecording(recordingId, draft);
            onSaved(updated.title);
            setEditing(false);
          } finally {
            setBusy(false);
          }
        }}
        className="inline-flex h-8 items-center rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
      >
        {busy ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => setEditing(false)}
        className="text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      >
        Cancel
      </button>
    </div>
  );
}

/**
 * A meeting's own title and description, in the header where a plain recording shows a
 * read-only `<h1>` — recordings have never been renamed, and a meeting is named by a
 * human at split time and may be renamed after (recording/meeting split).
 */
export function MeetingHeading({
  meeting,
  onSaved,
}: {
  meeting: SourceMaterial;
  onSaved: (updated: { title: string | null; description: string | null }) => void;
}) {
  const { api } = useApp();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(meeting.title ?? "");
  const [description, setDescription] = useState(meeting.description ?? "");
  const [busy, setBusy] = useState(false);

  if (!editing) {
    return (
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            {meeting.title ?? "Untitled meeting"}
          </h1>
          <button
            type="button"
            onClick={() => {
              setTitle(meeting.title ?? "");
              setDescription(meeting.description ?? "");
              setEditing(true);
            }}
            aria-label="Rename or describe this meeting"
            className="text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <Pencil size={16} strokeWidth={2} />
          </button>
        </div>
        {meeting.description && (
          <p className="max-w-[70ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            {meeting.description}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-2)]">
      <input
        autoFocus
        type="text"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="Meeting name"
        className="h-10 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-3 text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]"
      />
      <textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="Description (optional)"
        rows={2}
        className="resize-none rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-3 py-2 text-[length:var(--text-sm)] text-[var(--text-primary)]"
      />
      <div className="flex items-center gap-[var(--space-3)]">
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const updated = await api.updateMeeting(meeting.id, { title, description });
              onSaved(updated);
              setEditing(false);
            } finally {
              setBusy(false);
            }
          }}
          className="inline-flex h-8 items-center rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setEditing(false)}
          className="text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ meetings

// ------------------------------------------------------------------ proposed meetings
